"use client";

import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/tailwind/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/tailwind/ui/dialog";
import { Input } from "@/components/tailwind/ui/input";
import { Label } from "@/components/tailwind/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tailwind/ui/tabs";
import { Textarea } from "@/components/tailwind/ui/textarea";

export interface FieldConfig {
  key: string;
  label: string;
  type: "text" | "number" | "url" | "select" | "textarea";
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  section?: string;
}

interface ItemDialogProps<T> {
  title: string;
  description: string;
  buttonText: string;
  fields: FieldConfig[];
  template: T;
  onSubmit: (data: T, isEdit: boolean) => Promise<void>;
  validateRequired?: (data: Partial<T>) => { valid: boolean; message?: string };
  editData?: T | null;
  onOpenChange?: (open: boolean) => void;
}

export default function ItemDialog<T extends Record<string, any>>({
  title,
  description,
  buttonText,
  fields,
  template,
  onSubmit,
  validateRequired,
  editData = null,
  onOpenChange,
}: ItemDialogProps<T>) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<T>>(editData || template);
  const [jsonInput, setJsonInput] = useState("");
  const [jsonError, setJsonError] = useState("");
  const [loading, setLoading] = useState(false);
  const isEditMode = !!editData;

  // Watch for editData changes and open dialog when it's set
  useEffect(() => {
    if (editData) {
      setFormData(editData);
      setJsonInput(JSON.stringify(editData, null, 2));
      setJsonError("");
      setOpen(true);
      if (onOpenChange) {
        onOpenChange(true);
      }
    }
  }, [editData, onOpenChange]);

  const handleFieldChange = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleFormSubmit = async () => {
    if (validateRequired) {
      const validation = validateRequired(formData);
      if (!validation.valid) {
        setJsonError(validation.message || "Validation failed");
        return;
      }
    }

    setLoading(true);
    try {
      await onSubmit(formData as T, isEditMode);
      handleClose();
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : "Failed to save item");
    } finally {
      setLoading(false);
    }
  };

  const handleJsonSubmit = async () => {
    try {
      const parsed = JSON.parse(jsonInput);

      if (validateRequired) {
        const validation = validateRequired(parsed);
        if (!validation.valid) {
          setJsonError(validation.message || "Validation failed");
          return;
        }
      }

      setLoading(true);
      await onSubmit(parsed as T, isEditMode);
      handleClose();
    } catch (error) {
      if (error instanceof SyntaxError) {
        setJsonError("Invalid JSON format");
      } else {
        setJsonError(error instanceof Error ? error.message : "Failed to save item");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleJsonInputChange = (value: string) => {
    setJsonInput(value);
    setJsonError("");
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(template);
    setJsonInput("");
    setJsonError("");
    if (onOpenChange) {
      onOpenChange(false);
    }
  };

  const handleDialogClose = (isOpen: boolean) => {
    if (isOpen) {
      setFormData(editData || template);
      setJsonInput(editData ? JSON.stringify(editData, null, 2) : "");
      setJsonError("");
    } else {
      handleClose();
    }
    setOpen(isOpen);
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  };

  // Group fields by section
  const sections = fields.reduce(
    (acc, field) => {
      const section = field.section || "main";
      if (!acc[section]) acc[section] = [];
      acc[section].push(field);
      return acc;
    },
    {} as Record<string, FieldConfig[]>,
  );

  const renderField = (field: FieldConfig) => {
    const value = formData[field.key as keyof T] ?? "";

    switch (field.type) {
      case "select":
        return (
          <select
            value={value || ""}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="" disabled>
              {field.placeholder || "Select an option"}
            </option>
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      case "textarea":
        return (
          <Textarea
            value={String(value)}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            rows={3}
          />
        );
      case "number":
        return (
          <Input
            type="number"
            value={String(value)}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
          />
        );
      default:
        return (
          <Input
            type={field.type}
            value={String(value)}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
          />
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus className="h-4 w-4 mr-2" />
          {buttonText}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{isEditMode ? `Edit ${title.replace("Add New ", "")}` : title}</DialogTitle>
          <DialogDescription>
            {isEditMode ? `Edit the ${title.replace("Add New ", "").toLowerCase()}.` : description}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="form" className="w-full flex flex-col flex-1 overflow-hidden">
          <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
            <TabsTrigger value="form">Form</TabsTrigger>
            <TabsTrigger value="json">JSON</TabsTrigger>
          </TabsList>

          <TabsContent value="form" className="space-y-4 mt-4 overflow-y-auto flex-1">
            {Object.entries(sections).map(([sectionName, sectionFields]) => (
              <div key={sectionName}>
                {sectionName !== "main" && (
                  <div className="border-t pt-4 mb-3">
                    <h4 className="text-sm font-medium capitalize">{sectionName.replace(/_/g, " ")}</h4>
                  </div>
                )}
                <div className="grid gap-4">
                  {sectionFields.map((field) => (
                    <div key={field.key} className="grid gap-2">
                      <Label htmlFor={field.key}>
                        {field.label}
                        {field.required && <span className="text-red-500"> *</span>}
                      </Label>
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {jsonError && <div className="text-sm text-red-500 mt-2">{jsonError}</div>}
          </TabsContent>

          <TabsContent value="json" className="space-y-4 mt-4 overflow-y-auto flex-1">
            <div className="space-y-2">
              <Label htmlFor="json-input">Paste JSON</Label>
              <Textarea
                id="json-input"
                value={jsonInput}
                onChange={(e) => handleJsonInputChange(e.target.value)}
                placeholder={JSON.stringify(template, null, 2)}
                rows={15}
                className="font-mono text-sm"
              />
              {jsonError && <div className="text-sm text-red-500">{jsonError}</div>}
              <div className="text-xs text-gray-500">Paste a complete JSON object matching the template structure</div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={() => handleDialogClose(false)} disabled={loading}>
            Cancel
          </Button>
          <Tabs defaultValue="form" className="hidden" />
          <Button
            onClick={() => {
              const activeTab = document.querySelector('[role="tabpanel"][data-state="active"]');
              const isJsonTab = activeTab?.id.includes("json");
              if (isJsonTab) {
                handleJsonSubmit();
              } else {
                handleFormSubmit();
              }
            }}
            disabled={loading}
          >
            {loading ? (isEditMode ? "Saving..." : "Creating...") : isEditMode ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
